import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Image as ImageIcon, MapPin } from "lucide-react";

export default function Photos() {
  const [photos, setPhotos] = useState([]);
  const [memories, setMemories] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [p, m, c] = await Promise.all([api.get("/photos"), api.get("/memories"), api.get("/chapters")]);
    setPhotos(p.data); setMemories(m.data); setChapters(c.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("caption", "");
      await api.post("/photos", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photograph added — now add a caption and choose where it belongs"); load();
    } catch { toast.error("Couldn't upload that photo. Please try again."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveCaption = async (id, caption) => {
    try { await api.put(`/photos/${id}`, { caption }); toast.success("Caption saved"); }
    catch { toast.error("Couldn't save the caption."); }
  };
  const setPlacement = async (id, value) => {
    const [kind, refId] = value.split(":");
    const body = kind === "chapter"
      ? { chapter_id: refId, memory_id: "" }
      : kind === "memory"
        ? { memory_id: refId, chapter_id: "" }
        : { chapter_id: "", memory_id: "" };
    try {
      await api.put(`/photos/${id}`, body);
      setPhotos((ps) => ps.map((p) => p.id === id ? { ...p, ...body } : p));
      toast.success(kind === "none" ? "Placement cleared" : "Photograph placed");
    } catch { toast.error("Couldn't save that just now."); }
  };
  const del = async (id) => { if (!window.confirm("Remove this photograph?")) return; await api.delete(`/photos/${id}`); load(); };

  const placementValue = (p) => p.chapter_id ? `chapter:${p.chapter_id}` : p.memory_id ? `memory:${p.memory_id}` : "none";
  const appearsIn = (p) => {
    if (p.chapter_id) {
      const c = chapters.find((c) => c.id === p.chapter_id);
      return c ? `Appears in the chapter “${c.title}”` : null;
    }
    if (p.memory_id) {
      const m = memories.find((m) => m.id === p.memory_id);
      return m ? `Appears with your memory “${(m.question_text || "").slice(0, 40)}”` : null;
    }
    return null;
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Photographs</h1>
          <p className="text-subtle mt-1">Pictures to bring your memoir to life.</p>
        </div>
        <button data-testid="upload-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60">
          {uploading ? <><Loader2 className="animate-spin" size={16} /> Uploading…</> : <><Upload size={16} /> Upload</>}
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="photo-file-input" />
      </div>

      <div className="bg-sagelight border border-sage/30 rounded-2xl p-4">
        <p className="text-sm text-charcoal/85 leading-relaxed">
          <b className="text-teal">How photographs work:</b> upload a picture, add an optional caption, then choose the memory or
          chapter it belongs with. Placed photographs are printed inside that chapter when you download your Word or PDF book.
        </p>
      </div>

      {photos.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-line">
          <ImageIcon className="mx-auto text-sage" size={36} />
          <p className="mt-4 text-subtle">Upload family photographs, places and faces from your story.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {photos.map((p) => (
            <div key={p.id} data-testid={`photo-${p.id}`} className="bg-white rounded-2xl border border-line overflow-hidden animate-fade-up">
              <div className="aspect-video bg-cream2 overflow-hidden">
                <img src={fileUrl(p.file_id)} alt={p.caption || "memory"} className="w-full h-full object-cover" />
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="text-xs font-medium text-charcoal">Caption <span className="text-subtle font-normal">(optional)</span></label>
                  <input
                    data-testid={`photo-caption-${p.id}`} defaultValue={p.caption} placeholder="e.g. Mum and me on the beach, 1959"
                    onBlur={(e) => saveCaption(p.id, e.target.value)}
                    className="mt-1 w-full h-10 px-3 text-sm rounded-lg border border-line bg-white outline-none focus:border-teal text-charcoal placeholder:text-subtle/60"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-charcoal">Where does it belong?</label>
                  <select data-testid={`photo-placement-${p.id}`} value={placementValue(p)} onChange={(e) => setPlacement(p.id, e.target.value)}
                    className="mt-1 w-full h-10 px-3 text-sm rounded-lg border border-line bg-white outline-none focus:border-teal text-charcoal">
                    <option value="none">Not placed yet</option>
                    {chapters.length > 0 && (
                      <optgroup label="Chapters">
                        {chapters.map((c) => <option key={c.id} value={`chapter:${c.id}`}>Chapter: {c.title}</option>)}
                      </optgroup>
                    )}
                    {memories.length > 0 && (
                      <optgroup label="Memories">
                        {memories.map((m) => <option key={m.id} value={`memory:${m.id}`}>Memory: {(m.question_text || "").slice(0, 45)}</option>)}
                      </optgroup>
                    )}
                  </select>
                  {appearsIn(p) && (
                    <p data-testid={`photo-appears-${p.id}`} className="mt-2 text-xs text-teal flex items-center gap-1.5"><MapPin size={13} /> {appearsIn(p)}</p>
                  )}
                </div>
                <button data-testid={`delete-photo-${p.id}`} onClick={() => del(p.id)} className="text-subtle hover:text-destructive inline-flex items-center gap-1.5 text-sm"><Trash2 size={14} /> Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
